import React, { useEffect, useState } from "react";
import {
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Grid,
  FormControlLabel,
  Checkbox,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@material-ui/core";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { makeStyles } from "@material-ui/core/styles";
import { blue } from "@material-ui/core/colors";
import PhotoCamera from "@material-ui/icons/PhotoCamera";

/* ------------------------- Importaciones internas ------------------------- */
import { api } from "../../../services/api.js";
import DialogFooter from "../../../components/DialogFooter/DialogActions";
import BotonGenerico from "../../../components/Button/Button";
import { useAuth } from "../../../contexts/AuthContext";

/* --------------------------------- Estilos -------------------------------- */
const useStyles = makeStyles((theme) => ({
  profileBox: {
    padding: theme.spacing(2),
    borderRadius: "15px",
    display: "flex",
    flexDirection: "column",
  },
  title: {
    textAlign: "center",
    fontWeight: "bold",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  inputLabel: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  textField: {
    marginBottom: theme.spacing(1),
    "& input": {
      borderRadius: "8px",
      backgroundColor: "white",
      padding: theme.spacing(1),
    },
    backgroundColor: "#4caf50",
    borderRadius: "8px",
  },
  textFieldPass: {
    marginBottom: theme.spacing(1),
    "& input": {
      backgroundColor: "white",
      borderRadius: "8px",
      padding: theme.spacing(1),
    },
    backgroundColor: "#4caf50",
    borderRadius: "8px",
  },
  photoInput: {
    display: "none",
  },
  photoPreview: {
    width: "100px",
    height: "100px",
    borderRadius: "50%",
    margin: theme.spacing(2),
    objectFit: "cover",
  },
  iconButton: {
    color: blue[500],
  },
  saveButton: {
    backgroundColor: "#4caf50",
    color: "white",
    borderRadius: "8px",
    width: "100%",
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#45a049",
    },
  },
  validationMessage: {
    color: "green",
    fontSize: "0.8rem",
  },
}));

const ProfileSettingsModal = ({ open, onClose }) => {
  const classes = useStyles();
  const MAX_PASSWORD_LENGTH = 15;

  // Usuario local
  const { getUser } = useAuth();
  const user = getUser();

  const formDefaultValues = {
    name: "",
    surname: "",
    email: "",
    password: "",
    newPassword: "",
    confirmPassword: "",
    notifications: true,
    photo: null,
    passwordValidations: {
      newPassword: "",
      confirmPassword: "",
      error: "",
      success: false,
    },
  };
  const [form, setForm] = useState(formDefaultValues);
  const [passBd, setPassBd] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirm, setShowNewPasswordConfirm] = useState(false);

  const validatePassword = (password) => {
    const validations = {
      length: password.length >= 8 && password.length <= MAX_PASSWORD_LENGTH,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
    };
    setForm((prev) => ({
      ...prev,
      passwordValidations: validations,
    }));
    return Object.values(validations).every(Boolean);
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file)
      setForm((prev) => ({ ...prev, photo: URL.createObjectURL(file) }));
  };

  const handleSave = async () => {
    const {
      name,
      surname,
      email,
      password,
      newPassword,
      confirmPassword,
      photo,
    } = form;

    if (password !== passBd) {
      alert("La contraseña actual no es correcta.");
      return;
    }

    if (newPassword !== confirmPassword) {
      alert("Las contraseñas no coinciden.");
      return;
    }

    if (!validatePassword(newPassword)) {
      alert("La nueva contraseña no cumple con los requisitos.");
      return;
    }

    try {
      let photoPath = null;
      if (photo) {
        const formData = new FormData();
        const fileBlob = await fetch(photo).then((res) => res.blob());
        formData.append(
          "file",
          fileBlob,
          `${name.replace(/\s+/g, "_").toLowerCase()}_profile.jpg`
        );
        const responsePhoto = await api.UplooadPhoto(formData);
        if (!responsePhoto.success) throw new Error(responsePhoto.message);
        photoPath = responsePhoto.filePath;
      }

      const userData = {
        Nombre: name,
        Apellido: surname,
        email,
        Contraseña: newPassword,
        fechaRegistro: new Date().toISOString(),
        IdSector: 1,
        foto: photoPath,
      };

      const response = await api.usuarios.create(userData);
      if (response.success) {
        alert("Usuario registrado con éxito.");
        onClose();
      } else {
        alert("Error al registrar: " + response.message);
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Ocurrió un error. Intenta nuevamente.");
    }
  };

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        try {
          const perfilResponse = await api.usuarios.getById(user.IdUsuario);

          setForm((prev) => ({
            ...prev,
            name: perfilResponse.Nombre || "",
            surname: perfilResponse.Apellido || "",
            email: perfilResponse.email || "",
          }));

          setPassBd(perfilResponse.Contraseña);
        } catch (error) {
          console.error("Error al obtener perfil:", error.message);
        }
      };

      fetchData();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setForm(formDefaultValues);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Ajustes de Perfil</DialogTitle>
      <DialogContent className={classes.profileBox}>
        <Grid container justify="center" alignItems="center">
          <input
            accept="image/*"
            className={classes.photoInput}
            id="profile-photo"
            type="file"
            onChange={handlePhotoChange}
          />
          <label htmlFor="profile-photo">
            <IconButton
              color="primary"
              component="span"
              className={classes.iconButton}
            >
              <PhotoCamera />
            </IconButton>
          </label>
          {!form.photo ? (
            <Avatar
              style={{
                width: 100,
                height: 100,
                margin: "16px auto",
                backgroundColor: "#ccc",
              }}
            >
              P
            </Avatar>
          ) : (
            <img
              src={form.photo}
              alt="Foto de perfil"
              className={classes.photoPreview}
            />
          )}
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography className={classes.inputLabel}>Nombre</Typography>
            <TextField
              variant="outlined"
              fullWidth
              className={classes.textField}
              value={form.name}
              // onChange={(e) => handleChange("name")(e)}
              onChange={(e) => {
                const value = e.target.value;
                setForm((prev) => ({ ...prev, name: value }));
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.inputLabel}>Apellidos</Typography>
            <TextField
              variant="outlined"
              fullWidth
              className={classes.textField}
              value={form.surname}
              // onChange={(e) => handleChange("surname")(e)}
              onChange={(e) => {
                const value = e.target.value;
                setForm((prev) => ({ ...prev, surname: value }));
              }}
            />
          </Grid>
        </Grid>

        <Typography className={classes.inputLabel}>
          Correo Electrónico
        </Typography>
        <TextField
          type="email"
          variant="outlined"
          fullWidth
          className={classes.textField}
          value={form.email}
          // onChange={(e) => handleChange("email")(e)}
          onChange={(e) => {
            const value = e.target.value;
            setForm((prev) => ({ ...prev, email: value }));
          }}
        />

        <Typography className={classes.inputLabel}>
          Contraseña Actual
        </Typography>
        <TextField
          type={showPassword ? "text" : "password"}
          variant="outlined"
          fullWidth
          className={classes.textFieldPass}
          value={form.password}
          // onChange={(e) => handleChange("password")(e)}
          onChange={(e) => {
            const value = e.target.value;
            setForm((prev) => ({
              ...prev,
              password: value.slice(0, MAX_PASSWORD_LENGTH),
            }));
          }}
          inputProps={{ maxLength: MAX_PASSWORD_LENGTH }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword((prev) => !prev)}>
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Typography className={classes.validationMessage}>
          {form.password.length}/{MAX_PASSWORD_LENGTH}
        </Typography>

        <Typography className={classes.inputLabel}>Nueva Contraseña</Typography>
        <TextField
          type={showNewPassword ? "text" : "password"}
          variant="outlined"
          fullWidth
          className={classes.textFieldPass}
          value={form.newPassword}
          // onChange={(e) => handleChange("newPassword")(e)}
          onChange={(e) => {
            const value = e.target.value;
            setForm((prev) => ({
              ...prev,
              newPassword: value.slice(0, MAX_PASSWORD_LENGTH),
            }));
          }}
          inputProps={{ maxLength: MAX_PASSWORD_LENGTH }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNewPassword((prev) => !prev)}>
                  {showNewPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Typography className={classes.validationMessage}>
          Máximo {MAX_PASSWORD_LENGTH} caracteres. {form.newPassword.length}/{MAX_PASSWORD_LENGTH}
        </Typography>

        {!form.passwordValidations.length && (
          <Typography className={classes.validationMessage}>
            Debe tener entre 8 y {MAX_PASSWORD_LENGTH} caracteres.
          </Typography>
        )}
        {!form.passwordValidations.uppercase && (
          <Typography className={classes.validationMessage}>
            Debe tener al menos una mayúscula.
          </Typography>
        )}
        {!form.passwordValidations.lowercase && (
          <Typography className={classes.validationMessage}>
            Debe tener al menos una minúscula.
          </Typography>
        )}
        {!form.passwordValidations.number && (
          <Typography className={classes.validationMessage}>
            Debe tener al menos un número.
          </Typography>
        )}

        <Typography className={classes.inputLabel}>
          Confirmar Contraseña
        </Typography>
        <TextField
          type={showNewPasswordConfirm ? "text" : "password"}
          variant="outlined"
          fullWidth
          className={classes.textFieldPass}
          value={form.confirmPassword}
          // onChange={(e) => handleChange("confirmPassword")(e)}
          onChange={(e) => {
            const value = e.target.value;
            setForm((prev) => ({
              ...prev,
              confirmPassword: value.slice(0, MAX_PASSWORD_LENGTH),
            }));
          }}
          inputProps={{ maxLength: MAX_PASSWORD_LENGTH }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowNewPasswordConfirm((prev) => !prev)}
                >
                  {showNewPasswordConfirm ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Typography className={classes.validationMessage}>
          {form.confirmPassword.length}/{MAX_PASSWORD_LENGTH}
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={form.notifications}
              // onChange={(e) => handleChange("notifications")(e)}
              onChange={(e) => {
                const value = e.target.checked;
                setForm((prev) => ({ ...prev, notifications: value }));
              }}
              color="primary"
            />
          }
          label="Recibir Notificaciones"
        />
      </DialogContent>
      <DialogFooter>
        <BotonGenerico
          tipo="guardar"
          text="Guardar Cambios"
          onClick={handleSave}
        />
        <BotonGenerico color="secondary" text="Cancelar" onClick={onClose} />
      </DialogFooter>
    </Dialog>
  );
};

export default ProfileSettingsModal;
